/**
 * AppModal — the ONLY modal in this app. Never render `Modal` from
 * 'react-native' directly.
 *
 * WHY THIS EXISTS
 *
 * On Android, `components/ui/Pressable` resolves to react-native-gesture-
 * handler's Pressable, which renders a `GestureDetector`. A gesture handler
 * only receives touches through an orchestrator created by a
 * `GestureHandlerRootView` in the SAME native window: RNGH walks up the view
 * tree looking for a `RootView` and gives up otherwise. React Native's `Modal`
 * mounts into a separate Dialog window, and the app's only
 * `GestureHandlerRootView` lives in `components/RootLayoutApp.tsx`, in the main
 * window.
 *
 * The result is not "one dead button". A transparent full-screen Modal whose
 * own backdrop cannot be tapped is a lid over the whole app: the screen behind
 * it, its toasts and the tab bar are all unreachable, and only the Android back
 * gesture escapes. That is exactly the "the whole screen stops responding to
 * touch" report — one defect, not three.
 *
 * The repo had already met this hazard three times and solved it three
 * different ways (ConfirmDialog wrapped a root view; DatePicker and Select
 * swapped to RN's native Pressable with a comment restating the rule). Three
 * divergent remedies for one rule is why the fourth site — BlockUserButton —
 * was missed. So the remedy now lives in one primitive, and
 * `scripts/validate-modal-gesture-root.mjs` fails the build if a raw
 * `react-native` Modal comes back.
 *
 * PRESENTATION
 *
 * RN's `animationType="slide"` moves the entire modal window, so a painted
 * dimmer rides the sheet. That is not how iOS presents: the mask fades, the
 * sheet slides, and they do not share a transform. This primitive owns that
 * split for every transparent `slide` / `fade` overlay:
 *
 *   - native Modal always mounts with `animationType="none"`
 *   - `maskOpacity` fades the scrim (~DURATION.fast, cubic ease)
 *   - `sheetProgress` translates only the children (slide), never the mask
 *   - closing fades the mask out in place — the dimmer does not slide away
 *
 * Full-screen / page-sheet presentations and `animationType="none"` keep the
 * native window animation. `useReducedMotion()` skips the slide and snaps or
 * short-cuts the fade.
 *
 * Props are RN's `ModalProps` verbatim — this is a drop-in replacement.
 */
import React, { useEffect, useRef, useState } from 'react'
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  useWindowDimensions,
  type ModalProps,
} from 'react-native'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { useTheme } from '../../lib/hooks/use-theme'
import { DURATION, useSemanticColors } from '../../lib/theme/tokens'

export interface AppModalProps extends ModalProps {
  children: React.ReactNode
}

const MASK_IN = { duration: DURATION.fast, easing: Easing.out(Easing.cubic) }
const MASK_OUT = { duration: DURATION.fast, easing: Easing.in(Easing.cubic) }
const SHEET_IN = { duration: DURATION.normal, easing: Easing.out(Easing.cubic) }
const SHEET_OUT = { duration: DURATION.normal, easing: Easing.in(Easing.cubic) }

function usesIndependentMask({
  transparent,
  animationType,
  presentationStyle,
}: Pick<ModalProps, 'transparent' | 'animationType' | 'presentationStyle'>): boolean {
  if (!transparent) return false
  if (animationType !== 'slide' && animationType !== 'fade') return false
  if (
    presentationStyle === 'pageSheet' ||
    presentationStyle === 'formSheet' ||
    presentationStyle === 'fullScreen'
  ) {
    return false
  }
  return true
}

export function AppModal({
  children,
  visible = false,
  animationType = 'none',
  transparent,
  presentationStyle,
  onRequestClose,
  onShow,
  onDismiss,
  ...modalProps
}: AppModalProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion() === true
  const { height: windowHeight } = useWindowDimensions()
  const independentMotion = usesIndependentMask({
    transparent,
    animationType,
    presentationStyle,
  })

  const [rendered, setRendered] = useState(visible)
  const renderedRef = useRef(visible)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const maskOpacity = useSharedValue(0)
  const sheetProgress = useSharedValue(animationType === 'slide' ? 0 : 1)
  const contentOpacity = useSharedValue(animationType === 'fade' ? 0 : 1)

  useEffect(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }

    if (!independentMotion) {
      renderedRef.current = visible
      setRendered(visible)
      maskOpacity.value = visible ? 1 : 0
      sheetProgress.value = 1
      contentOpacity.value = 1
      return
    }

    if (visible) {
      renderedRef.current = true
      setRendered(true)
      if (reducedMotion) {
        maskOpacity.value = 1
        sheetProgress.value = 1
        contentOpacity.value = 1
        return
      }
      maskOpacity.value = withTiming(1, MASK_IN)
      if (animationType === 'slide') {
        contentOpacity.value = 1
        sheetProgress.value = withTiming(1, SHEET_IN)
      } else {
        sheetProgress.value = 1
        contentOpacity.value = withTiming(1, MASK_IN)
      }
      return
    }

    if (!renderedRef.current) return

    const finishHide = () => {
      renderedRef.current = false
      hideTimeoutRef.current = null
      setRendered(false)
    }

    if (reducedMotion) {
      maskOpacity.value = 0
      sheetProgress.value = animationType === 'slide' ? 0 : 1
      contentOpacity.value = animationType === 'fade' ? 0 : 1
      finishHide()
      return
    }

    maskOpacity.value = withTiming(0, MASK_OUT)
    if (animationType === 'slide') {
      contentOpacity.value = 1
      sheetProgress.value = withTiming(0, SHEET_OUT)
    } else {
      sheetProgress.value = 1
      contentOpacity.value = withTiming(0, MASK_OUT)
    }
    const waitMs =
      animationType === 'slide' ? Math.max(DURATION.fast, DURATION.normal) : DURATION.fast
    hideTimeoutRef.current = setTimeout(finishHide, waitMs)

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
        hideTimeoutRef.current = null
      }
    }
  }, [
    animationType,
    contentOpacity,
    independentMotion,
    maskOpacity,
    reducedMotion,
    sheetProgress,
    visible,
  ])

  const maskStyle = useAnimatedStyle(() => ({
    opacity: maskOpacity.value,
  }))
  const sheetStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: (1 - sheetProgress.value) * windowHeight }],
  }))
  const contentFadeStyle = useAnimatedStyle(() => ({
    opacity: contentOpacity.value,
  }))

  const nativeAnimationType = independentMotion ? 'none' : animationType
  const modalVisible = independentMotion ? rendered : visible

  return (
    <Modal
      {...modalProps}
      visible={modalVisible}
      transparent={transparent}
      presentationStyle={presentationStyle}
      animationType={nativeAnimationType}
      onRequestClose={onRequestClose}
      onShow={onShow}
      onDismiss={onDismiss}
    >
      {/*
        A Modal is a separate native window, so a KeyboardAwareView on the page
        cannot resize it. Keeping keyboard avoidance here makes searchable
        sheets, confirmation forms and payment forms obey the same viewport
        contract on iOS and Android. `flex: 1` keeps both wrappers layout-neutral
        while the keyboard is closed.
      */}
      <GestureHandlerRootView style={{ flex: 1 }}>
        {independentMotion ? (
          <Animated.View
            pointerEvents="none"
            testID="app-modal.mask"
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            style={[
              {
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                backgroundColor: colors.scrim,
              },
              maskStyle,
            ]}
          />
        ) : null}
        <KeyboardAvoidingView
          style={{ flex: 1, overflow: 'hidden' }}
          behavior={
            Platform.OS === 'ios' ? 'padding' : Platform.OS === 'android' ? 'height' : undefined
          }
          enabled={Platform.OS !== 'web'}
        >
          {independentMotion ? (
            <Animated.View style={[{ flex: 1 }, sheetStyle, contentFadeStyle]}>
              {children}
            </Animated.View>
          ) : (
            children
          )}
        </KeyboardAvoidingView>
      </GestureHandlerRootView>
    </Modal>
  )
}
